import {
  serializeTransaction,
  type TransactionSerializable,
  type TransactionSerializableEIP1559,
  type TransactionSerializableEIP2930,
  type TransactionSerializableLegacy,
  type TransactionSerializableEIP4844,
} from 'viem';
import type { EthereumTransaction } from '@txviz/core';
import { hexToBytes } from '@txviz/core';

/**
 * Serialize an Ethereum transaction into its canonical unsigned wire format.
 *
 * This produces the exact bytes that keccak256 is applied to before signing.
 * Uses viem's serializeTransaction which handles:
 * - Legacy: RLP([nonce, gasPrice, gasLimit, to, value, data, chainId, 0, 0])
 * - EIP-2930: 0x01 || RLP([chainId, nonce, gasPrice, gasLimit, to, value, data, accessList])
 * - EIP-1559: 0x02 || RLP([chainId, nonce, maxPriorityFeePerGas, maxFeePerGas, gasLimit, to, value, data, accessList])
 * - EIP-4844: 0x03 || RLP([chainId, nonce, maxPriorityFeePerGas, maxFeePerGas, gasLimit, to, value, data, accessList, maxFeePerBlobGas, blobVersionedHashes])
 */
export function serializeEthereumTransaction(tx: EthereumTransaction): Uint8Array {
  const viemTx = toViemTransaction(tx);
  // serializeTransaction without a signature produces the unsigned form
  const serializedHex = serializeTransaction(viemTx);
  return hexToBytes(serializedHex);
}

/**
 * Convert our typed transaction to viem's TransactionSerializable.
 */
function toViemTransaction(tx: EthereumTransaction): TransactionSerializable {
  const base = {
    chainId: Number(tx.chainId),
    nonce: Number(tx.nonce),
    to: (tx.to ?? undefined) as `0x${string}` | undefined,
    value: tx.value,
    data: tx.data as `0x${string}`,
    gas: tx.gasLimit,
  };

  switch (tx.type) {
    case 'legacy': {
      return {
        ...base,
        type: 'legacy',
        gasPrice: tx.gasPrice,
      } satisfies TransactionSerializableLegacy;
    }
    case 'eip2930': {
      return {
        ...base,
        type: 'eip2930',
        gasPrice: tx.gasPrice,
        accessList: tx.accessList.map((entry) => ({
          address: entry.address as `0x${string}`,
          storageKeys: entry.storageKeys as `0x${string}`[],
        })),
      } satisfies TransactionSerializableEIP2930;
    }
    case 'eip1559': {
      return {
        ...base,
        type: 'eip1559',
        maxFeePerGas: tx.maxFeePerGas,
        maxPriorityFeePerGas: tx.maxPriorityFeePerGas,
        accessList: tx.accessList.map((entry) => ({
          address: entry.address as `0x${string}`,
          storageKeys: entry.storageKeys as `0x${string}`[],
        })),
      } satisfies TransactionSerializableEIP1559;
    }
    case 'eip4844': {
      return {
        ...base,
        to: tx.to as `0x${string}`,
        type: 'eip4844',
        maxFeePerGas: tx.maxFeePerGas,
        maxPriorityFeePerGas: tx.maxPriorityFeePerGas,
        accessList: tx.accessList.map((entry) => ({
          address: entry.address as `0x${string}`,
          storageKeys: entry.storageKeys as `0x${string}`[],
        })),
        maxFeePerBlobGas: tx.maxFeePerBlobGas,
        blobVersionedHashes: tx.blobVersionedHashes as `0x${string}`[],
      } as TransactionSerializableEIP4844;
    }
  }
}
